import { FetchResult } from "@apollo/client";
import {
  Button,
  ButtonMenu,
  Css,
  FilterDefs,
  Tooltip,
  useGridTableApi,
  useModal,
  usePersistedFilter,
  useSnackbar,
  useSuperDrawer,
  useTestIds,
  useToast,
} from "@homebound/beam";
import { useCallback, useEffect, useState } from "react";
import { createProjectFinishScheduleUrl, createProjectSelectionDetailsUrl } from "src/RouteUrls";
import {
  ApplyItemTemplateInput,
  EstimateType,
  ProjectFeature,
  ProjectItemFilter,
  ProjectItemInput,
  Stage,
} from "src/generated/graphqlTypes";
import { buildLocationsNestedOptions } from "src/hooks/useProjectLocationsTree";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import {
  SpecsAndSelectionsFilter,
  createSpecsAndSelectionsFilterDef,
} from "src/routes/projects/components/SpecsAndSelectionsFilterModal";
import { SpecsAndSelectionsTable } from "src/routes/projects/components/SpecsAndSelectionsTable";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectItemsContextProvider, useProjectItemsStore } from "src/routes/projects/context/ProjectItemsContext";
import { CreateEstimateModal } from "src/routes/projects/estimates/CreateEstimateModal";
import { SpecsAndSelectionsRow } from "src/routes/projects/models/ProjectItemStore";
import { AddProjectItemModal, AddSingleItemInput } from "src/routes/projects/selections/AddProjectItemModal";
import { SpecsAndSelectionsHeaderTag } from "src/routes/projects/selections/SpecsAndSelectionsHeaderTag";
import {
  SaveProjectItemsMutation,
  SpecsAndSelectionItemsQuery,
  useAddProjectItemMutation,
  useApplyItemTemplateMutation,
  useSaveProjectItemsMutation,
  useSpecsAndSelectionItemsQuery,
} from "src/routes/projects/selections/SpecsAndSelectionsPage.generated";
import { SpecsAndSelectionsBulkActionsMenu } from "src/routes/projects/selections/bulkActions/SpecsAndSelectionsBulkActionsMenu";
import { ProjectParams } from "src/routes/routesDef";
import { queryResult } from "src/utils/queryResult";
import { useQueryParams } from "use-query-params";
import { AddLaborItemFormInput, AddLaborItemModal } from "../components/AddLaborItemModal";
import { AddMaterialItemFormInput, AddMaterialItemModal } from "../components/AddMaterialItemModal";
import { LotConfigurationDetailsStaticField } from "../components/LotConfigurationDetailsStaticField";
import { getIsLocationRequired } from "../components/utils";
import { ApplyTemplateModal } from "./ApplyTemplateModal";
import { ImportProjectItemsModal } from "./ImportProjectItemsModal";

export function SpecsAndSelectionsPage({ stage }: { stage: Stage }) {
  const { projectId } = useRouteParams<ProjectParams>();
  const { features } = useProjectContext();

  const [filterSet, setFilterSet] = useState(false);
  const [filterDefs, setFilterDefs] = useState<FilterDefs<SpecsAndSelectionsFilter>>(() => {
    // Even though we don't know the cost codes/locations yet, make an initial filter def
    // so that usePersistedFilter can validate any url/session storage values.
    return createSpecsAndSelectionsFilterDef(stage, [], []);
  });
  const { filter: uiFilter, setFilter } = usePersistedFilter({ filterDefs, storageKey: "projectItemFilter" });

  const enableProductConfigPlan = features.includes(ProjectFeature.ProductConfigPlan);
  const gqlFilter = toGqlFilter(uiFilter);
  const query = useSpecsAndSelectionItemsQuery({
    variables: {
      projectId,
      stage,
      filter: gqlFilter,
      costCodeFilter: { version: enableProductConfigPlan ? [2] : [1] },
    },
    fetchPolicy: "network-only",
  });

  // Stop observing cache updates so auto-save does not re-initialize ProjectItemStore and its observers.
  // Variable changes reactivate the query with its network-only policy.
  useEffect(() => {
    if (!query.data) return;
    void query.observable.reobserve({ fetchPolicy: "standby" });
  }, [query.data, query.observable]);

  // We can't create filterDefs until the 1st query comes back with the metadata; in theory
  // we should probably break these out into separate GQL queries so we're not re-fetching the
  // metadata each time they change a filter.
  const currentData =
    // Entering standby clears data, but Apollo retains the delivered network result in previousData.
    query.data ?? query.previousData;
  if (!filterSet && currentData) {
    const locationOptions = buildLocationsNestedOptions(currentData.project.stage.itemLocations);
    setFilterDefs(createSpecsAndSelectionsFilterDef(stage, currentData.costCodes, locationOptions));
    setFilterSet(true);
  }

  const { closeDrawer, isDrawerOpen } = useSuperDrawer();
  /**
   * When un-mounting this page, close the Superdrawer.
   * This is to prevent having a SuperDrawer stay open when navigating multiple
   * pages back/forward as the SuperDrawer is outside the ReactRouter scope.
   */
  useEffect(() => {
    return function unmountSpecsAndSelectionsPage() {
      isDrawerOpen && closeDrawer();
    };
  }, [closeDrawer, isDrawerOpen]);

  return queryResult(query, (data) => (
    <ProjectItemsContextProvider>
      <SpecsAndSelectionsPageContent
        data={data}
        projectId={projectId}
        stage={stage}
        filterDefs={filterDefs}
        uiFilter={uiFilter}
        gqlFilter={gqlFilter}
        setFilter={setFilter}
        enableProductConfigPlan={enableProductConfigPlan}
      />
    </ProjectItemsContextProvider>
  ));
}

type SpecsAndSelectionsPageContentProps = {
  data: SpecsAndSelectionItemsQuery;
  projectId: string;
  stage: Stage;
  filterDefs: FilterDefs<SpecsAndSelectionsFilter>;
  uiFilter: SpecsAndSelectionsFilter;
  gqlFilter: ProjectItemFilter;
  setFilter: (filter: SpecsAndSelectionsFilter) => void;
  enableProductConfigPlan: boolean;
};

function SpecsAndSelectionsPageContent(props: SpecsAndSelectionsPageContentProps) {
  const { data, projectId, stage, filterDefs, uiFilter, gqlFilter, setFilter, enableProductConfigPlan } = props;
  const { project, unitsOfMeasure, scheduleTasks } = data;
  const { projectItems, id: projectStageId, hasSignedContract, canEditProjectItemsDirectly } = project.stage;
  const api = useGridTableApi<SpecsAndSelectionsRow>();
  const { store } = useProjectItemsStore();
  const [saveProjectItems] = useSaveProjectItemsMutation();
  const [applyItemTemplate] = useApplyItemTemplateMutation();
  const [addProjectItem] = useAddProjectItemMutation();
  const [, setQueryParams] = useQueryParams({});
  const { openModal } = useModal();
  const { showToast } = useToast();
  const { triggerNotice } = useSnackbar();
  const tid = useTestIds({});
  // Use plan or legacy schedule tasks based on the project configuration
  const tasks = enableProductConfigPlan
    ? project.planSchedule?.tasks.map((t) => ({ ...t, status: t.status.code }))
    : scheduleTasks;

  const onSave = useCallback(
    async (...inputs: ProjectItemInput[]) => {
      const result = await saveProjectItems({ variables: { input: { projectItems: [...inputs] } } });
      const userErrors = result.data?.saveProjectItems?.userErrors;
      const errorText = userErrors && userErrors.map((e) => e.message).join(" ");
      if (errorText) {
        console.error(errorText);
        triggerNotice({ message: errorText, icon: "error" });
      }
      return !Boolean(errorText);
      // TODO: This should update the local store, not refetch the entire thing...
    },
    [saveProjectItems, triggerNotice],
  );

  const createProjectItemUrl = useCallback(
    (projectItemId: string) => createProjectSelectionDetailsUrl(projectId, stage, projectItemId),
    [projectId, stage],
  );

  async function handleAddItem(
    input: AddSingleItemInput | AddLaborItemFormInput | AddMaterialItemFormInput,
  ): Promise<void> {
    const { costType, locationId, unitOfMeasureId, quantity } = input;
    const result = await addProjectItem({
      variables: {
        input: {
          projectStageId,
          ...("proposedTaskId" in input
            ? { globalPlanTaskId: input.proposedTaskId, locationId, costType, unitOfMeasureId, quantity }
            : "materialVariantId" in input
              ? {
                  materialVariantId: input.materialVariantId,
                  globalPlanTaskId: input.taskId,
                  locationId,
                  costType,
                  unitOfMeasureId,
                  quantity,
                }
              : input),
        },
        filter: gqlFilter,
      },
    });
    await handleSaveProjectItems(result);
  }

  // Called by both handleAddItem and ImportProjectItemsModal
  async function handleSaveProjectItems(result: FetchResult<SaveProjectItemsMutation>) {
    const userErrors = result.data?.saveProjectItems?.userErrors;
    if (userErrors) {
      showToast({ type: "error", message: userErrors.map((e) => e.message).join(", ") });
      console.error(userErrors);
    }
    const items = result.data?.saveProjectItems.projectItems;
    if (items) {
      store.addProjectItems(items);
      setQueryParams({ projectItemId: items[0].id }, "replaceIn");
    }
  }

  async function onApplyTemplate(input: ApplyItemTemplateInput) {
    const result = await applyItemTemplate({ variables: { input, filter: gqlFilter } });
    const userErrors = result.data?.applyItemTemplate?.userErrors;
    if (userErrors) {
      console.error(userErrors);
    }
    const items = result.data?.applyItemTemplate.projectItems;
    if (items) {
      store.addProjectItems(items);
      setQueryParams({ projectItemId: items[0]?.id }, "replaceIn");
    }
  }
  const locationRequired = getIsLocationRequired(project.__typename ?? "");

  const addOptions = enableProductConfigPlan
    ? [
        {
          label: "By Material",
          onClick: () =>
            openModal({
              content: (
                <AddMaterialItemModal onAdd={handleAddItem} locationRequired={locationRequired} projectId={projectId} />
              ),
            }),
        },
        {
          label: "By Labor",
          onClick: () =>
            openModal({
              content: (
                <AddLaborItemModal onAdd={handleAddItem} locationRequired={locationRequired} projectId={projectId} />
              ),
            }),
        },
      ]
    : [
        {
          label: "From Template",
          onClick: () =>
            openModal({
              content: (
                <ApplyTemplateModal projectStageId={project.stage.id} onApplyTemplate={onApplyTemplate} stage={stage} />
              ),
            }),
        },
        {
          label: "From Import",
          onClick: () =>
            openModal({
              content: <ImportProjectItemsModal onImport={handleSaveProjectItems} projectStageId={project.stage.id} />,
            }),
        },
        { label: "New", onClick: () => openModal({ content: <AddProjectItemModal onAdd={handleAddItem} /> }) },
      ];

  const hasProjectItemsAdded = projectItems.length !== 0;

  // The overflowHidden styles in this file are to prevent the scrollable container in ProjectLayout from being displayed here.
  // The flex styles allow for the contents of this element to take up the remainder of the space allotted by the h100 defined in PageLayout.
  return (
    <div {...tid.specsAndSelectionsPage}>
      <PageHeader
        xss={Css.bgWhite.$}
        title={
          project.__typename === "StandaloneProject"
            ? stage === Stage.PreConExecution
              ? "Pre-Con Services"
              : "Specs & Selections"
            : "Scope"
        }
        left={hasSignedContract !== undefined && <SpecsAndSelectionsHeaderTag hasSignedContract={hasSignedContract} />}
        right={
          <>
            {enableProductConfigPlan && (
              <Button label="Finish Schedule" onClick={createProjectFinishScheduleUrl(projectId)} variant="tertiary" />
            )}
            <Tooltip
              title="Project stage has a signed contract or an estimate already exists"
              disabled={canEditProjectItemsDirectly}
              placement="left"
            >
              <ButtonMenu disabled={!canEditProjectItemsDirectly} items={addOptions} trigger={{ label: "Add Items" }} />
            </Tooltip>
            <Tooltip
              title="Project stage has a signed contract, an estimate already exists or there are no project items added"
              disabled={hasProjectItemsAdded && canEditProjectItemsDirectly}
              placement="left"
            >
              <Button
                label="Create Estimate"
                disabled={!hasProjectItemsAdded || !canEditProjectItemsDirectly}
                onClick={() => {
                  openModal({
                    content: (
                      <CreateEstimateModal
                        initialFormState={{
                          title: `${
                            project.stage.stage.code === Stage.Construction ? "Construction" : "Pre-Construction"
                          } Estimate`,
                          type: EstimateType.Final,
                        }}
                        projectId={projectId}
                        projectStage={project.stage}
                      />
                    ),
                  });
                }}
              />
            </Tooltip>
          </>
        }
      />

      <div css={Css.oh.$}>
        <div css={Css.df.gap6.mb4.$}>
          {project.stage.readyHomePlanTemplate && (
            <LotConfigurationDetailsStaticField projectId={project.id} data-testid="planInfo" />
          )}
        </div>
        <SpecsAndSelectionsTable
          bulkActionsMenu={
            <SpecsAndSelectionsBulkActionsMenu
              canEditProjectItemsDirectly={canEditProjectItemsDirectly}
              hasSignedContract={hasSignedContract}
              onSave={onSave}
              projectId={project.id}
              projectStageId={project.stage.id}
              scheduleTasks={tasks || []}
              store={store}
              api={api}
            />
          }
          createProjectItemUrl={createProjectItemUrl}
          mode={canEditProjectItemsDirectly ? "S&S" : "S&S-PartialReadOnly"}
          onSave={onSave}
          projectItems={projectItems}
          filterDefs={filterDefs}
          uiFilter={uiFilter}
          setFilter={setFilter}
          stage={stage}
          scheduleTasks={tasks || []}
          unitsOfMeasure={unitsOfMeasure}
          api={api}
        />
      </div>
    </div>
  );
}

/** Adjust `locationId`, before sending the actual filter to the backend. */
function toGqlFilter(uiFilter: SpecsAndSelectionsFilter): ProjectItemFilter {
  const { locationId, unallocatedCosts, ...rest } = uiFilter;
  const gqlFilter: ProjectItemFilter = { isRemovedScope: false, ...rest };

  if (locationId) {
    // Use `locationInPath` (ancestor-path match) rather than `location.id`
    // (exact match) so selecting a group/room node like "Bathroom 1" also
    // includes ProjectItems on its descendant locations.
    gqlFilter.locationInPath = locationId;
  }
  return gqlFilter;
}
